// Recipient routing category — which agency contact receives a given notification.
export const EMAIL_TYPES = {
  CONFIRMATION_BOOKING_INVOICE: 'confirmation-booking-invoice',
};

// Canonical identifiers for every email the platform sends. Senders tag a send with one of
// these (via `meta.type`) so it is recorded on the EmailLog `type` field; untagged sends fall
// back to 'unknown'. Distinct from EMAIL_TYPES above, which categorizes recipients.
export const EMAIL_LOG_TYPES = {
  RESET_PASSWORD_AGENT: 'reset-password-agent',
  RESET_PASSWORD_CLIENT: 'reset-password-client',
  RESET_PASSWORD_PARTNER: 'reset-password-partner',
  VERIFY_AGENT: 'verify-agent',
  VERIFY_CLIENT: 'verify-client',
  LEAD: 'lead',
  ENQUIRY_EXAMPLE_CLIENT: 'enquiry-example-client',
  ENQUIRY_EXAMPLE_WIZARD: 'enquiry-example-wizard',
  REQUEST_HOLD: 'request-hold',
  QUOTE_ITINERARY: 'quote-itinerary',
  HOLD_ITINERARY: 'hold-itinerary',
  CANCEL_HOLD_ITINERARY: 'cancel-hold-itinerary',
  CONFIRM_ITINERARY: 'confirm-itinerary',
  CONFIRM_BOOKING: 'confirm-booking',
  ITINERARY_PROBLEM: 'itinerary-problem',
  PAYMENT_RECEIPT_AGENT: 'payment-receipt-agent',
  PAYMENT_RECEIPT_CLIENT: 'payment-receipt-client',
  COST_CHANGE: 'agent-cost-changes',
  CONSULTANT_MESSAGE: 'consultant-message',
  INTERNATIONAL_FLIGHTS: 'international-flights',
  INVOICE_GENERATION: 'invoice-generation',
  INVOICES_UPDATED: 'invoices-updated',
  WIRE_TRANSFER_DETAILS: 'wire-transfer-details',
  CHARGE_EVENT_ALERT: 'charge-event-alert',
};

// Email types that exist in the registry (so a stray or harness-triggered send still logs under its
// real type) but are not live emails the platform sends to a real recipient. They are excluded from
// the email administration console and are not part of the templated-email program. A type not
// listed here is active.
//
// Two grounds for inactivity:
//
// Inactive by design — reset-password-partner: partners are service/API-key accounts with no login
// or reset flow, so the inherited User reset never fires in the product.
//
// Retired dead trigger — confirm-itinerary, quote-itinerary and itinerary-problem have no product UI
// path that reaches them. confirm-itinerary needs POST /WindowAPIs/ConfirmQuote, which no component
// calls (Itinerary.confirmBooking is the live confirm path); quote-itinerary needs sendNotify, which
// every UI path leaves false; itinerary-problem has no product sender at all.
//
// The first two stay callable over REST — WindowAPI grants Agent and Partner blanket access plus
// Client an explicit grant on getQuote, and GetQuote defaults sendNotify to true while ModifyQuote
// hardcodes it. So retirement rests on the
// absence of a UI path plus 15+ months of silence in the send history, not on unreachability. That
// is what makes it a soft retirement: listing a type here changes no send behavior. Neither
// resolveEmailRoute nor the trigger harness consults this list, so a send that does happen still
// renders, delivers and logs under its real type — it is only absent from the console.
export const INACTIVE_EMAIL_LOG_TYPES = [
  EMAIL_LOG_TYPES.RESET_PASSWORD_PARTNER,
  EMAIL_LOG_TYPES.CONFIRM_ITINERARY,
  EMAIL_LOG_TYPES.QUOTE_ITINERARY,
  EMAIL_LOG_TYPES.ITINERARY_PROBLEM,
];

// Whether an email type is a live email the platform sends and the admin console manages. Durable:
// it describes the email's nature, not its position in the SendGrid migration.
export const isEmailLogTypeActive = type => !INACTIVE_EMAIL_LOG_TYPES.includes(type);

// The engine that renders and sends an email. Legacy is the EJS/SES path; SendGrid renders a
// dynamic template server-side. The migration adds SendGrid alongside legacy per type; the endgame
// is SendGrid only.
export const EMAIL_ENGINES = {
  LEGACY: 'legacy',
  SENDGRID: 'sendgrid',
};

// Types whose message exists only as a SendGrid dynamic template. They have no EJS body, so the
// legacy engine has nothing to render for them and a legacy send puts a blank email on the wire that
// still logs as delivered. Declaring a type here makes SendGrid its only route: an absent config row
// reads as SendGrid rather than legacy, the send chokepoint refuses rather than falling back, and
// the admin console offers no legacy engine. A type qualifies once it has a SendGrid template and a
// payload builder and no legacy template of its own.
const SENDGRID_ONLY_EMAIL_LOG_TYPES = [EMAIL_LOG_TYPES.CHARGE_EVENT_ALERT];

// Whether SendGrid is an email type's only route. Keyed off the type rather than off anything the
// caller supplies, so a fresh send, an admin resend and a harness trigger all reach the same answer,
// including for a log row written before the type was declared.
export const isSendgridOnlyEmailType = type => SENDGRID_ONLY_EMAIL_LOG_TYPES.includes(type);

// The engine a logged send was ORIGINALLY sent with. SendGrid sends carry a `mode`
// (stub/redirect/live); legacy sends have none, so a missing mode means legacy.
export const emailRowEngine = row => (row && row.mode ? EMAIL_ENGINES.SENDGRID : EMAIL_ENGINES.LEGACY);

// The engine a type is CURRENTLY configured to use, from its EmailTypeConfig row. A row that stores
// no preference falls to the type's own default: SendGrid for a SendGrid-only type, legacy for
// every other, matching what resolveEmailRoute does with the same absence.
export const emailConfigEngine = (config, type) => {
  const stored = config && config.useSendgrid;
  if (stored === true || stored === false) {
    return stored ? EMAIL_ENGINES.SENDGRID : EMAIL_ENGINES.LEGACY;
  }
  return isSendgridOnlyEmailType(type) ? EMAIL_ENGINES.SENDGRID : EMAIL_ENGINES.LEGACY;
};

// Token emails carry a one-time link (reset/verify). Replaying the stored send (resend) re-sends a
// stale link, so these are regenerate-only — regenerating mints a fresh, valid token.
const TOKEN_EMAIL_LOG_TYPES = [
  EMAIL_LOG_TYPES.RESET_PASSWORD_AGENT,
  EMAIL_LOG_TYPES.RESET_PASSWORD_CLIENT,
  EMAIL_LOG_TYPES.RESET_PASSWORD_PARTNER,
  EMAIL_LOG_TYPES.VERIFY_AGENT,
  EMAIL_LOG_TYPES.VERIFY_CLIENT,
];

// Types that can be regenerated: re-triggered as a fresh send from source, through the type's
// current engine. Turned on per type as it migrates to SendGrid and gains a real re-trigger path
// (the harness throwaway triggers are not production-safe). Grows across the migration.
const REGENERATABLE_EMAIL_LOG_TYPES = [EMAIL_LOG_TYPES.RESET_PASSWORD_AGENT, EMAIL_LOG_TYPES.RESET_PASSWORD_CLIENT];

// The admin console's two per-type send operations. `canResend` replays the exact stored send (a
// frozen snapshot); it is offered for non-token types, and the runtime additionally requires the
// row's origin engine to match the type's current engine. `canRegenerate` re-triggers a fresh send
// from source. A type absent from both lists is a resendable, not-yet-regeneratable non-token.
export const emailTypeOperations = type => ({
  canResend: !TOKEN_EMAIL_LOG_TYPES.includes(type),
  canRegenerate: REGENERATABLE_EMAIL_LOG_TYPES.includes(type),
});
